import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROLE_NAV, ROLE_LABELS, canViewInvestors } from '../../config/constants';
import * as api from '../../utils/api';

const NAV_ITEMS = [
  { key: 'dashboard',    label: 'Дашборд',        icon: '◈', path: '/dashboard' },
  { key: 'crm',          label: 'CRM',            icon: '◉', path: '/crm' },
  { key: 'advertising',  label: 'Реклама',        icon: '✦', path: '/advertising' },
  { key: 'bookings',     label: 'Бронирования',   icon: '▣', path: '/bookings' },
  // Внешний сервис отзывов и KPI (отдельный VPS, свой вход) — открывается в новой вкладке.
  { key: 'reviews',      label: 'Отзывы',          icon: '★', href: 'https://reviews.grandora.ru' },
  { key: 'transactions', label: 'Журнал',          icon: '↕', path: '/transactions' },
  { key: 'bank_import',  label: 'Импорт Т-Банк',  icon: '⇓', path: '/bank-import' },
  { key: 'reports',      label: 'Отчёты',          icon: '▦', path: '/reports' },
  { key: 'investors',    label: 'Инвесторам',      icon: '◆', path: '/investors' },
  { key: 'payroll',      label: 'Зарплата',        icon: '₽', path: '/payroll' },
  { key: 'breakfasts',   label: 'Учёт завтраков',  icon: '☕', path: '/breakfasts' },
  { key: 'settings',     label: 'Справочники',     icon: '⚙', path: '/settings' },
  { key: 'users',        label: 'Пользователи',    icon: '⊛', path: '/users' },
  { key: 'audit_log',    label: 'Лог операций',    icon: '◎', path: '/audit' },
];

const navLinkStyle = isActive => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 20px',
  textDecoration: 'none',
  fontSize: 'var(--text-sm)',
  fontWeight: isActive ? 'var(--font-semibold)' : 'var(--font-normal)',
  color: isActive ? 'var(--satin-50)' : 'var(--satin-300)',
  background: isActive ? 'var(--bg-dark-hover)' : 'transparent',
  borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
  transition: 'var(--transition-fast)',
});

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = user ? (ROLE_NAV[user.role] || []) : [];
  const canSeeImport = allowed.includes('bank_import');

  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!canSeeImport) return;
    const load = () => api.get('/import/pending-count').then(r => setPendingCount(r.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [canSeeImport, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <aside style={{
      width: 'var(--sidebar-width)',
      minHeight: '100vh',
      background: 'var(--bg-dark)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
    }}>
      {/* Логотип */}
      <div style={{
        padding: '24px 20px 16px',
        borderBottom: '1px solid var(--border-dark)',
      }}>
        <div style={{
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--font-black)',
          color: 'var(--text-on-dark)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>ГРАНДОРА</div>
        <div style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-on-dark-secondary)',
          marginTop: 2,
          letterSpacing: '0.04em',
        }}>МЕНЕДЖЕР</div>
      </div>

      {/* Навигация */}
      <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
        {NAV_ITEMS.filter(item =>
          // «Инвесторам» — единственный пункт с точечным доступом: по username
          // (полный) или инвестор с привязанным держателем (только по себе,
          // 02.10.2026) — canViewInvestors, не по роли (см. constants.js).
          item.key === 'investors' ? canViewInvestors(user) : allowed.includes(item.key)
        ).map(item => item.href ? (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            style={navLinkStyle(false)}
          >
            <span style={{ fontSize: 16, lineHeight: 1, opacity: 0.8 }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            <span style={{ fontSize: 11, opacity: 0.6 }}>↗</span>
          </a>
        ) : (
          <NavLink
            key={item.key}
            to={item.path}
            style={({ isActive }) => navLinkStyle(isActive)}
          >
            <span style={{ fontSize: 16, lineHeight: 1, opacity: 0.8 }}>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.key === 'bank_import' && pendingCount > 0 && (
              <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px', fontWeight: 'var(--font-semibold)', lineHeight: 1.4 }}>
                {pendingCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Пользователь */}
      <div style={{
        padding: '16px 20px',
        borderTop: '1px solid var(--border-dark)',
      }}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-on-dark)', fontWeight: 'var(--font-medium)' }}>
          {user?.name}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-on-dark-secondary)', marginTop: 2 }}>
          {ROLE_LABELS[user?.role]}
        </div>
        <button
          onClick={handleLogout}
          style={{
            marginTop: 12,
            padding: '6px 12px',
            background: 'transparent',
            border: '1px solid var(--border-dark)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--satin-300)',
            fontSize: 'var(--text-xs)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Выйти
        </button>
      </div>
    </aside>
  );
}
